import React, { FC, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./components/ui/button";
import "./styles/globals.css";

const OrgEqTerms: FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Set page title
    document.title = "Terms of Service - OrgEQ";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Terms of Service for OrgEQ - Review the terms and conditions for using the OrgEQ employee experience and sentiment analytics platform.');
    }
  }, []);

  const formatSection = (content: string) => {
    const formatted: JSX.Element[] = [];
    
    // Remove title from content if present
    let workingContent = content.replace(/^Leadoff\.ai Terms and Conditions\s*\n\s*/i, '');
    
    // Normalize line breaks - handle multiple cases of concatenated content
    
    // 1. Split items after colons without spaces: "through:Website" -> "through:\nWebsite"
    workingContent = workingContent.replace(/(:\s*)([A-Z][a-zA-Z\s]+:)/g, '$1\n$2');
    
    // 2. Split items separated by period+capital letter (key-value items): "description.Word:"
    workingContent = workingContent.replace(/(\.)([A-Z][a-zA-Z\s]+:)/g, '$1\n$2');
    
    // 3. Split sentences concatenated without spaces: "promptly.Customer" -> "promptly.\nCustomer"
    // Handle period followed by capital letter (general case)
    workingContent = workingContent.replace(/([a-z])(\.)([A-Z][a-z])/g, '$1$2\n$3');
    
    // 4. Split items after closing paren: "inquiries)Address:" -> "inquiries)\nAddress:"
    workingContent = workingContent.replace(/(\))([A-Z][a-zA-Z\s]+:)/g, '$1\n$2');
    
    // 5. Split key-value pairs that are on the same line: "Word1: desc.Word2: desc"
    // (handles cases where period+capital starts a new key-value item)
    workingContent = workingContent.replace(/([.!?])([A-Z][a-zA-Z\s]+:)/g, '$1\n$2');
    
    // Split by numbered sections - handle multi-digit numbers correctly
    // Find all section boundaries by matching section numbers at start of line
    const sectionMatches: Array<{index: number, number: string}> = [];
    const sectionPattern = /^(\d+)\.\s+/gm;
    let match;
    
    while ((match = sectionPattern.exec(workingContent)) !== null) {
      sectionMatches.push({
        index: match.index,
        number: match[1]
      });
    }
    
    // Split content at section boundaries
    const sections: string[] = [];
    for (let i = 0; i < sectionMatches.length; i++) {
      const startIndex = sectionMatches[i].index;
      const endIndex = i < sectionMatches.length - 1 ? sectionMatches[i + 1].index : workingContent.length;
      const sectionContent = workingContent.substring(startIndex, endIndex).trim();
      if (sectionContent) {
        sections.push(sectionContent);
      }
    }
    
    // If no sections found with the manual approach, fall back to regex split
    if (sections.length === 0) {
      const fallbackSections = workingContent.split(/(?=^\d+\.\s+)/m).map(s => s.trim()).filter(s => s);
      return formatSections(fallbackSections);
    }
    
    return formatSections(sections);
  };

  const formatSections = (sections: string[]) => {
    const formatted: JSX.Element[] = [];
    
    sections.forEach((section, sectionIdx) => {
      const lines = section.split('\n').filter(l => l.trim());
      let currentSectionContent: JSX.Element[] = [];
      let currentList: string[] = [];
      let inList = false;
      let expectingListAfterSubheader = false; // Track if we're expecting list items after a subsection header
      
      lines.forEach((line, lineIdx) => {
        const trimmed = line.trim();
        if (!trimmed) return;
        
        // Section header (e.g., "1. Introduction")
        if (/^\d+\.\s+/.test(trimmed) && lineIdx === 0) {
          // Close any open list from previous section
          if (inList && currentList.length > 0) {
            currentSectionContent.push(
              <ul key={`list-${sectionIdx}-${lineIdx}-pre`} className="list-disc ml-6 mb-4 space-y-2">
                {currentList.map((item, idx) => (
                  <li key={idx} className="text-gray-700">{item}</li>
                ))}
              </ul>
            );
            currentList = [];
            inList = false;
          }
          
          currentSectionContent.push(
            <h2 key={`header-${sectionIdx}`} className="text-2xl font-bold text-gray-900 mt-12 mb-6 first:mt-0">
              {trimmed}
            </h2>
          );
          return;
        }
        
        // Subsection header (ends with colon, not numbered, short line)
        if (trimmed.endsWith(':') && !trimmed.match(/^\d+\.|^[a-z]\)|^-/) && trimmed.length < 100 && !trimmed.includes('.')) {
          // Close any open list
          if (inList && currentList.length > 0) {
            currentSectionContent.push(
              <ul key={`list-${sectionIdx}-${lineIdx}`} className="list-disc ml-6 mb-4 space-y-2">
                {currentList.map((item, idx) => (
                  <li key={idx} className="text-gray-700">{item}</li>
                ))}
              </ul>
            );
            currentList = [];
            inList = false;
          }
          
          currentSectionContent.push(
            <h3 key={`subheader-${sectionIdx}-${lineIdx}`} className="text-xl font-semibold text-gray-800 mt-6 mb-3">
              {trimmed}
            </h3>
          );
          
          // Set flag that we're expecting list items after this header
          expectingListAfterSubheader = true;
          return;
        }
        
        // List items starting with dash or letter
        if (/^[-•]\s+/.test(trimmed) || /^[a-z]\)\s+/.test(trimmed)) {
          const cleanItem = trimmed.replace(/^[-•]\s*|[a-z]\)\s*/, '');
          if (cleanItem) {
            currentList.push(cleanItem);
            inList = true;
          }
          return;
        }
        
        // Key-value items (pattern: "Word: description")
        const keyValuePattern = /^[A-Z][a-zA-Z\s]+:/;
        
        // Check if this is a standalone key-value item on its own line (like "Essential Cookies: description")
        if (keyValuePattern.test(trimmed) && trimmed.includes(':')) {
          // Treat as list item if we're expecting a list after a subsection header, or already in a list
          if (expectingListAfterSubheader || inList) {
            currentList.push(trimmed);
            inList = true;
            expectingListAfterSubheader = false; // Reset flag since we're now in a list
            return;
          }
        }
        
        // Check if line contains multiple key-value items separated by period+capital (legacy format)
        if (keyValuePattern.test(trimmed) && trimmed.match(/\.\s*[A-Z]/)) {
          const items = trimmed.split(/(?<=\.)\s*(?=[A-Z][a-zA-Z\s]+:)/).filter(i => i.trim());
          
          if (items.length > 1) {
            items.forEach(item => {
              const cleanItem = item.trim();
              if (cleanItem) {
                currentList.push(cleanItem);
                inList = true;
              }
            });
            return;
          }
        }
        
        // Check if we should close the list before a regular paragraph
        // Close list if we encounter a regular paragraph (not a key-value item)
        if (inList && currentList.length > 0 && !keyValuePattern.test(trimmed)) {
          // If this is a regular paragraph (not continuing the list), close the list
          if (trimmed.length > 50 || trimmed.endsWith('.')) {
            currentSectionContent.push(
              <ul key={`list-${sectionIdx}-${lineIdx}`} className="list-disc ml-6 mb-4 space-y-2">
                {currentList.map((item, idx) => (
                  <li key={idx} className="text-gray-700">{item}</li>
                ))}
              </ul>
            );
            currentList = [];
            inList = false;
            expectingListAfterSubheader = false; // Reset flag
          } else if (trimmed.length < 150) {
            // Short line might continue the list
            currentList.push(trimmed);
            return;
          }
        }
        
        // Regular paragraph - only process if not already handled above
        // Reset expectingListAfterSubheader if we hit a regular paragraph
        if (!keyValuePattern.test(trimmed) && !inList) {
          expectingListAfterSubheader = false; // Reset flag when we see a regular paragraph
          
          // Split long paragraphs into sentences if needed
          const sentences = trimmed.split(/(?<=[.!?])\s+(?=[A-Z])/).filter(s => s.trim());
          
          sentences.forEach((sentence, sentIdx) => {
            const cleanSentence = sentence.trim();
            if (cleanSentence) {
              currentSectionContent.push(
                <p key={`para-${sectionIdx}-${lineIdx}-${sentIdx}`} className="mb-4 text-gray-700 leading-relaxed">
                  {cleanSentence}
                </p>
              );
            }
          });
        }
      });
      
      // Close any remaining list
      if (inList && currentList.length > 0) {
        currentSectionContent.push(
          <ul key={`list-${sectionIdx}-end`} className="list-disc ml-6 mb-4 space-y-2">
            {currentList.map((item, idx) => (
              <li key={idx} className="text-gray-700">{item}</li>
            ))}
          </ul>
        );
      }
      
      // Reset flags for next section
      expectingListAfterSubheader = false;
      
      formatted.push(...currentSectionContent);
    });
    
    return formatted;
  };

  const termsContent = `Leadoff.ai Terms and Conditions

These Terms and Conditions ("Agreement"), including the Data Processing Addendum ("DPA"), are between the customer signing an Order Form ("Customer") and Leadoff.ai ("Leadoff.ai"), and are effective as of the date of execution by both Parties of the Order Form that incorporates these terms ("Effective Date"). This Agreement consists of these terms and conditions, as may be amended from time to time, and any Order Form that references these terms and is signed in writing by the Parties. By signing an Order Form, the Customer agrees to the following terms:

1. Definitions and Interpretations

1.1. "Leadoff.ai" refers to Leadoff.ai Inc., located at [Company Address], USA, with company registration number [Company Registration Number].

1.2. "Fees" encompass all charges paid or due under this Agreement, including those for the Services provided.

1.3. "Order Form" denotes a written agreement signed by both Parties detailing specific transactions for Services under this Agreement. Each Order Form represents a distinct legal agreement.

1.4. "Services" refer to the professional services, support, and any associated materials specified in an Order Form and provided by Leadoff.ai to the Customer as per this Agreement.

1.5. "Service Term" signifies the initial duration of the Services and any renewal periods as specified in the Order Form.

1.6. "Materials" include documents, reports, code, assessments, videos, simulations, product information, features, cloud services, and/or software provided to the Customer under this Agreement.

1.7. "License" means the limited right to use the Services as specified in the Order Form. This License does not transfer any intellectual property rights or ownership of the Materials or Services.

1.8. To the fullest extent permitted by applicable law, these terms apply to all interactions between the Customer and Leadoff.ai, irrespective of the platform or channel through which the Services are delivered or used, unless otherwise expressly agreed in writing by the Parties.

2. Parties and Agreement Basis

2.1. Each Party warrants that it has the full authority, power, and right to enter into this Agreement and to fulfill its obligations under this Agreement.

2.2. These terms govern the general conditions under which Leadoff.ai provides the Services, while the Order Form specifies the specific Services ordered by the Customer. In the event of any conflict between these documents, the terms of the Order Form shall prevail.

3. License, Duration, and Intellectual Property

3.1. Leadoff.ai grants the Customer a non-exclusive, non-transferable, non-assignable, and non-sublicensable License to use the Services solely for the Customer's internal business purposes during the Service Term as specified in the Order Form.

3.2. The initial Service Term will automatically renew for a period equal to the initial term unless either Party provides written notice of termination at least 30 days before the end of the current term. Non-payment of Fees does not constitute termination, and Leadoff.ai retains the right to seek payment for Fees due. Access to the Services will commence upon Leadoff.ai's receipt of a signed Order Form from the Customer.

3.3. Neither Party transfers any ownership rights to intellectual property under this Agreement. Leadoff.ai reserves all rights not expressly granted under this Agreement.

4. Leadoff.ai Responsibilities

4.1. Leadoff.ai will provide the Services to the Customer as outlined in the Order Form.

4.2. During the Service Term, Leadoff.ai will ensure the Services function as intended to support the training and operational needs of the Customer.

4.3. The Services will be compatible with the latest versions of major web browsers, including Chrome, Firefox, Safari, and Edge.

5. Customer Responsibilities

5.1. Throughout the Service Term, the Customer will:

5.1.1. Utilize the Services exclusively for internal business operations in accordance with the provided documentation and applicable laws. The Customer will not license, sublicense, sell, resell, rent, lease, transfer, assign, distribute, or otherwise commercially exploit or make the Services available to any third party. The Customer will not copy, reverse engineer, or attempt to discern the source code of the Services or any part thereof.

5.1.2. Furnish all necessary information required by Leadoff.ai to deliver the Services.

5.1.3. Ensure that all data and information provided to Leadoff.ai complies with all applicable laws and regulations, including those related to marketing and privacy.

6. Data Processing

6.1. If Leadoff.ai processes any Personal Data on behalf of the Customer, Leadoff.ai will act as the "data processor" and the Customer as the "data controller" under relevant data protection laws. The respective obligations of the Parties concerning Personal Data are detailed in the DPA.

7. Orders, Pricing, and Payments

7.1. The Customer agrees to pay Leadoff.ai the applicable fees specified in the Order Form. If the Customer's use of the Services exceeds the agreed capacity, additional fees will apply. Leadoff.ai reserves the right to adjust Fees or introduce new charges at the end of the current Service Term, with at least thirty (30) days' prior notice to the Customer. If the Customer disputes any billing, they must notify Leadoff.ai within 60 days of the invoice date.

7.2. Unless otherwise stated in the Order Form, Fees are payable in advance. Leadoff.ai may issue invoices up to 30 days before the start of the Service Term. Payments are due within 30 days from the invoice date.

7.3. Payments are processed via Stripe, Inc., with automatic billing for each renewal term. Payment details are securely stored with Stripe.

7.4. Orders and Fees are defined in the Order Form. Late payments will incur interest at a rate of 2% per month on the overdue amount.

7.5. Leadoff.ai reserves the right to suspend Services if payments are not made timely.

8. Limitation of Liability

8.1. Leadoff.ai is fully liable for damages caused by intentional misconduct, gross negligence, or injury to life, body, or health.

8.2. For minor negligence, Leadoff.ai's liability is limited to breaches of essential contractual obligations. These are obligations that enable the proper performance of the Agreement and on which the Customer relies.

8.3. In cases of minor negligence, Leadoff.ai is not liable for lost profits, lack of commercial success, or indirect damages.

8.4. Liability for minor negligence is limited to typical and foreseeable damages at the time the Agreement was made.

8.5. The limitations of liability also apply to Leadoff.ai's employees, agents, and subcontractors.

8.6. Liability under any applicable product liability laws remains unaffected.

9. Termination

9.1. Either Party may terminate an Order Form and this Agreement with 30 days' written notice before the end of the current Service Term. The Customer is not entitled to refunds for Fees already paid.

9.2. This Agreement may be terminated immediately if:

9.2.1. The other Party fails to remedy a material breach within 30 days of receiving written notice of the breach.

9.2.2. A force majeure event continues for more than 30 days after notice.

9.2.3. Either Party becomes insolvent, makes an assignment for the benefit of creditors, or has a receiver or similar officer appointed over its assets.

9.3. Leadoff.ai may terminate this Agreement immediately if the Customer fails to make timely payments, violates Section 5.1.1, or misuses Leadoff.ai's intellectual property or publishes unlawful material.

9.4. Upon termination, the Customer must cease all use of the Services. Provisions intended to survive termination, including payment obligations and liability limitations, will remain in effect.

10. Confidentiality

10.1. "Confidential Information" refers to any information disclosed by either Party (the "Disclosing Party") to the other Party (the "Recipient") that is marked as confidential or should reasonably be understood as confidential. This includes the terms of this Agreement, except for these GTC.

10.2. Confidential Information does not include information that is publicly known, was in the Recipient's possession without obligation of confidentiality, was disclosed to the Recipient by a third party without restriction, or was independently developed by the Recipient without reference to the Disclosing Party's Confidential Information.

10.3. The Recipient will use Confidential Information solely for the purposes of this Agreement and will protect it using the same degree of care as it uses for its own confidential information, but not less than a reasonable degree of care.

10.4. The Recipient may disclose Confidential Information if required by law, provided it gives the Disclosing Party prompt notice and limits disclosure to the extent possible.

11. Amendments

11.1. The Customer may request adjustments to the Services as follows:

11.1.1. Reductions in Services can be made after the current Service Term ends, with the same notice period required for termination.

11.1.2. Increases in Services can be requested at any time and will be accommodated as promptly as possible.

12. Assignment

12.1. Neither Party may assign this Agreement or any rights or obligations under it without the other Party's written consent, except that Leadoff.ai may assign this Agreement to a successor or affiliate without the Customer's consent. Any other assignment is void.

12.2. This Agreement is binding on the Parties' successors and assigns.

13. Entire Agreement

13.1. This Agreement constitutes the entire agreement between the Parties regarding its subject matter, superseding all prior agreements, understandings, and communications. Amendments must be in writing and signed by both Parties.

13.2. Any conflicting terms in the Customer's purchase orders or other documents are void. Actions by Leadoff.ai, such as accepting payments, do not make such terms binding.

14. Governing Law and Dispute Resolution

14.1. This Agreement is governed by the laws of the State of [Your State], USA, without regard to its conflict of laws principles.

14.2. Any disputes arising from or related to this Agreement shall be resolved in the courts of the State of [Your State].`;

  return (
    <div className="org-eq-dashboard min-h-screen bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 pointer-events-none opacity-20">
        <svg className="absolute w-full h-full" preserveAspectRatio="none">
          <defs>
            <linearGradient id="pulseGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#06b6d4">
                <animate
                  attributeName="stop-color"
                  values="#06b6d4; #14b8a6; #10b981; #06b6d4"
                  dur="8s"
                  repeatCount="indefinite"
                />
              </stop>
              <stop offset="50%" stopColor="#14b8a6">
                <animate
                  attributeName="stop-color"
                  values="#14b8a6; #10b981; #06b6d4; #14b8a6"
                  dur="8s"
                  repeatCount="indefinite"
                />
              </stop>
              <stop offset="100%" stopColor="#10b981">
                <animate
                  attributeName="stop-color"
                  values="#10b981; #06b6d4; #14b8a6; #10b981"
                  dur="8s"
                  repeatCount="indefinite"
                />
              </stop>
            </linearGradient>
          </defs>
          <path
            d="M-200 50 L-150 50 L-100 50 L-80 30 L-60 70 L-40 50 L0 50 L50 50 L100 50 L120 30 L140 70 L160 50 L200 50 L250 50 L300 50 L320 30 L340 70 L360 50 L400 50 L450 50 L500 50 L520 30 L540 70 L560 50 L600 50 L650 50 L700 50 L720 30 L740 70 L760 50 L800 50 L850 50 L900 50 L920 30 L940 70 L960 50 L1000 50 L1050 50 L1100 50 L1120 30 L1140 70 L1160 50 L1200 50 L1250 50 L1300 50 L1320 30 L1340 70 L1360 50 L1400 50 L1450 50 L1500 50 L1520 30 L1540 70 L1560 50 L1600 50 L1650 50 L1700 50 L1720 30 L1740 70 L1760 50 L1800 50 L1850 50 L1900 50 L1920 30 L1940 70 L1960 50 L2000 50"
            stroke="url(#pulseGradient)"
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <animateTransform
              attributeName="transform"
              type="translate"
              from="0 0"
              to="400 0"
              dur="4s"
              repeatCount="indefinite"
            />
          </path>
        </svg>
      </div>

      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-3">
            <img src="/logos/orgeq-logo.svg" alt="OrgEQ" className="h-14 w-auto" />
          </button>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => navigate("/org-eq/login")}>
              Sign In
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-6 py-12 lg:py-16 relative z-10">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border p-8 lg:p-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-8">Terms of Service</h1>
            
            <div className="prose prose-lg max-w-none">
              {formatSection(termsContent)}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default OrgEqTerms;

